// API Client for Northfolio
// Centralized API layer with auth

// Accept VITE_API_URL (legacy) or VITE_API_BASE_URL (preferred). Strip any
// trailing slash so '/auth/login' does not become '//auth/login'.
const RAW_API_URL = (
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  '/api'
);
const API_BASE_URL = String(RAW_API_URL).replace(/\/+$/, '');

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
}

async function fetchWithAuth(url, options = {}) {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Session expired. Please login again.');
    }
    
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `Error: ${response.status}`);
  }

  // Check if response has content
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return response.text();
}

// Auth API
export const authApi = {
  login: (username, password) => 
    fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  
  register: (username, email, password) =>
    fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    }),
  
  forgotPassword: (email) =>
    fetchWithAuth('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  
  resetPassword: (token, newPassword) =>
    fetchWithAuth('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
};

// Portfolio API
export const portfolioApi = {
  getPortfolio: () => fetchWithAuth('/portfolio'),
  getTrades: (limit = 5000) => fetchWithAuth(`/trades?limit=${limit}`),
  getDividends: () => fetchWithAuth('/dividends'),
  getDeposits: () => fetchWithAuth('/deposits'),
  
  createTrade: (data) => 
    fetchWithAuth('/trades', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateTrade: (id, data) =>
    fetchWithAuth(`/trades/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteTrade: (id) =>
    fetchWithAuth(`/trades/${id}`, {
      method: 'DELETE',
    }),
  
  createDividend: (data) =>
    fetchWithAuth('/dividends', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateDividend: (id, data) =>
    fetchWithAuth(`/dividends/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteDividend: (id) =>
    fetchWithAuth(`/dividends/${id}`, {
      method: 'DELETE',
    }),
  
  createDeposit: (data) =>
    fetchWithAuth('/deposits', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateDeposit: (id, data) =>
    fetchWithAuth(`/deposits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteDeposit: (id) =>
    fetchWithAuth(`/deposits/${id}`, {
      method: 'DELETE',
    }),
  
  getRisk: () => fetchWithAuth('/risk'),
};

// Analysis API
export const analysisApi = {
  getSummary: (positionType = 'open') => fetchWithAuth(`/analysis/summary?position_type=${positionType}`),
  getPnlCurve: (basis = 'REALIZED_PLUS_DIVIDENDS', benchmark = 'NONE', force = false) => {
    const params = new URLSearchParams();
    if (basis) params.append('basis', basis);
    if (benchmark && benchmark !== 'NONE') params.append('benchmark', benchmark);
    if (force) params.append('force', 'true');
    return fetchWithAuth(`/analysis/pnl-curve?${params}`);
  },
  getBenchmarks: (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return fetchWithAuth(`/analysis/benchmarks?${params}`);
  },
  getCashHistory: () => fetchWithAuth('/analysis/cash-history'),
  
  // Trade analytics
  getTradeStats: () => fetchWithAuth('/analysis/trade-stats'),
  getEquityCurve: (days = 90) => fetchWithAuth(`/analysis/equity-curve?days=${days}`),
  getClosedPositions: () => fetchWithAuth('/analysis/closed-positions'),
  // Persists today's portfolio value to equity_snapshots so max drawdown
  // and the equity curve have data to work with.
  saveSnapshot: () => fetchWithAuth('/analysis/save-snapshot', { method: 'POST' }),
};

// Metadata API
export const metadataApi = {
  getFxRate: (base = 'GBP', quote = 'EUR') =>
    fetchWithAuth(`/metadata/fx?base=${base}&quote=${quote}`),
  
  getSectors: (tickers) =>
    fetchWithAuth(`/metadata/sectors?tickers=${tickers.join(',')}`),
  
  searchTickers: (q, limit = 10) =>
    fetchWithAuth(`/metadata/ticker-search?q=${encodeURIComponent(q)}&limit=${limit}`),
};

// Tickers API (new professional search system)
export const tickersApi = {
  search: (q, limit = 10) =>
    fetchWithAuth(`/tickers/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  
  saveMapping: (mapping) =>
    fetchWithAuth('/tickers/mapping', {
      method: 'POST',
      body: JSON.stringify(mapping),
    }),
  
  getMapping: (internalTicker) =>
    fetchWithAuth(`/tickers/mapping/${encodeURIComponent(internalTicker)}`),
  
  listMappings: () =>
    fetchWithAuth('/tickers/mappings'),
};

// Health check
export const healthApi = {
  check: () => fetchWithAuth('/health'),
};
